/**
 * The Cooked Sushi — cart drawer sync (server totals are source of truth).
 */
window.NihooyaCart = window.NihooyaCart || {
  moneyFormat: window.Shopify?.money_format || '${{amount}}',
  _changeTimers: {},
  _cartSyncVersion: 0,
  _subtotalPending: false,

  discountIcon:
    '<svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12"><path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></path></svg>',

  minusIcon:
    '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-minus" fill="none" viewBox="0 0 10 2"><path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor"></path></svg>',

  plusIcon:
    '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-plus" fill="none" viewBox="0 0 10 10"><path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z" fill="currentColor"></path></svg>',

  getDrawer() {
    return document.querySelector('cart-drawer');
  },

  ensureDrawerReady() {
    const cartDrawer = this.getDrawer();
    if (!cartDrawer) return null;

    cartDrawer.classList.remove('is-empty');

    const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
    if (cartDrawerItems) cartDrawerItems.classList.remove('is-empty');

    const emptyPanel = cartDrawer.querySelector('.drawer__inner-empty');
    if (emptyPanel) emptyPanel.hidden = true;

    const wrapper = cartDrawer.querySelector('[data-cart-drawer-items]');
    if (wrapper) wrapper.hidden = false;

    const footer = cartDrawer.querySelector('.drawer__footer');
    if (footer) footer.hidden = false;

    const checkoutButton = cartDrawer.querySelector('#CartDrawer-Checkout');
    if (checkoutButton) checkoutButton.disabled = false;

    return cartDrawer.querySelector('.cart-items tbody');
  },

  openDrawer(triggeredBy) {
    const cartDrawer = this.getDrawer();
    if (!cartDrawer) return null;
    cartDrawer.open(triggeredBy || null);
    return cartDrawer;
  },

  formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const formatString = format || this.moneyFormat;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholder);
    if (!match) return String(cents);

    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (Number.isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    };

    let value = '';
    switch (match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholder, value);
  },

  parseMoneyFromDisplay(text) {
    if (!text) return 0;
    const cleaned = String(text).replace(/[^\d.,]/g, '').trim();
    if (!cleaned) return 0;

    let normalized = cleaned;
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');

    if (lastComma > -1 && lastDot > -1) {
      normalized =
        lastComma > lastDot
          ? cleaned.replace(/\./g, '').replace(',', '.')
          : cleaned.replace(/,/g, '');
    } else if (lastComma > -1) {
      const decimals = cleaned.length - lastComma - 1;
      normalized = decimals === 2 ? cleaned.replace(',', '.') : cleaned.replace(/,/g, '');
    } else {
      normalized = cleaned.replace(/,/g, '');
    }

    const value = parseFloat(normalized);
    return Number.isNaN(value) ? 0 : Math.round(value * 100);
  },

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  },

  getCurrencyCode() {
    const el =
      document.querySelector('[data-currency]') ||
      document.querySelector('.nihooya-cart-page .totals__subtotal-value') ||
      document.querySelector('.nihooya-cart-drawer .totals__subtotal-value');
    return el?.dataset.currency || '';
  },

  formatDisplayMoney(cents, format) {
    const money = this.formatMoney(cents, format);
    const currency = this.getCurrencyCode();
    if (!currency || currency === 'EUR' || money.includes(currency)) return money;
    return `${currency} ${money}`;
  },

  formatSubtotal(cents) {
    return this.formatDisplayMoney(cents);
  },

  setSubtotalPending(pending) {
    this._subtotalPending = pending;
    document.querySelectorAll('.nihooya-cart-drawer .totals__subtotal-value').forEach((node) => {
      node.classList.toggle('is-pending', pending);
      node.setAttribute('aria-busy', pending ? 'true' : 'false');
    });
  },

  updateCount(itemCount) {
    if (itemCount === undefined || itemCount === null) return;

    const count = parseInt(String(itemCount), 10) || 0;

    document.querySelectorAll('.nihooya-header__cart-badge').forEach((badge) => {
      badge.textContent = count;
      badge.classList.toggle('is-empty', count <= 0);
      badge.setAttribute('data-count', String(count));
    });

    document.querySelectorAll('.nihooya-cart-drawer__count, .nihooya-cart-page__count').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count <= 0;
    });

    document.querySelectorAll('.nihooya-cart-page__summary-count').forEach((node) => {
      node.textContent = count;
    });

    document.querySelectorAll('[data-nihooya-summary-items-label]').forEach((node) => {
      node.textContent = count === 1 ? 'item' : 'items';
    });

    const cartLink = document.getElementById('cart-icon-bubble');
    if (!cartLink) return;

    cartLink.querySelectorAll('.visually-hidden').forEach((node) => {
      if (node.textContent && /\d/.test(node.textContent)) {
        node.textContent = node.textContent.replace(/\d+/, String(count));
      }
    });
  },

  updateSubtotal(totalCents) {
    if (totalCents === undefined || totalCents === null) return;

    document.querySelectorAll('.nihooya-cart-drawer .totals__subtotal-value, .nihooya-cart-page .totals__subtotal-value').forEach((node) => {
      node.textContent = this.formatSubtotal(totalCents);
    });
  },

  updateDrawerFooter(cart) {
    if (!cart) return;
    this.updateSubtotal(cart.total_price);

    const container = document.getElementById('CartDrawer-Discounts');
    if (!container) return;

    const discounts = cart.cart_level_discount_applications || [];
    if (!discounts.length) {
      container.innerHTML = '';
      container.hidden = true;
      return;
    }

    container.hidden = false;
    container.innerHTML = `<ul class="discounts list-unstyled" role="list" aria-label="Discount">
      ${discounts
        .map(
          (discount) => `<li class="discounts__discount discounts__discount--end">
            ${this.discountIcon}
            ${this.escapeHtml(discount.title)}
            (-${this.formatDisplayMoney(discount.total_allocated_amount)})
          </li>`
        )
        .join('')}
    </ul>`;
  },

  getRowUnitCents(row) {
    if (!row) return 0;
    const stored = parseInt(row.dataset.unitPrice || '0', 10);
    if (stored > 0) return stored;

    const qty = parseInt(row.querySelector('.quantity__input')?.value || '1', 10) || 1;
    const lineCents = parseInt(row.dataset.linePrice || '0', 10);
    if (lineCents > 0 && qty > 0) {
      const unit = Math.round(lineCents / qty);
      if (unit > 0) {
        row.dataset.unitPrice = String(unit);
        return unit;
      }
    }

    const parsedLine = this.parseMoneyFromDisplay(row.querySelector('.cart-item__price-wrapper .price')?.textContent);
    const unit = qty > 0 ? Math.round(parsedLine / qty) : 0;
    if (unit > 0) row.dataset.unitPrice = String(unit);
    return unit;
  },

  buildLineDiscountsHTML(item) {
    const discounts = item.discounts || item.line_level_discount_allocations || [];
    if (!discounts.length) return '';

    return `<ul class="discounts list-unstyled" role="list" aria-label="Discount">
      ${discounts
        .map(
          (discount) => `<li class="discounts__discount">
            ${this.discountIcon}
            ${this.escapeHtml(discount.title)}
          </li>`
        )
        .join('')}
    </ul>`;
  },

  buildUnitPriceHTML(item) {
    const quantity = item.quantity || 1;
    if (quantity <= 1) return '';

    const original = item.original_price;
    const finalPrice = item.final_price ?? item.price ?? 0;

    if (original != null && original !== finalPrice) {
      return `<div class="nihooya-cart-drawer__unit-price cart-item__discounted-prices">
        <span class="visually-hidden">Regular price</span>
        <s class="cart-item__old-price product-option">${this.formatDisplayMoney(original)}</s>
        <span class="visually-hidden">Sale price</span>
        <strong class="cart-item__final-price product-option">${this.formatDisplayMoney(finalPrice)} each</strong>
      </div>`;
    }

    return `<div class="nihooya-cart-drawer__unit-price product-option">${this.formatDisplayMoney(finalPrice)} each</div>`;
  },

  buildLinePriceHTML(item) {
    const original = item.original_line_price;
    const finalLine = item.final_line_price ?? item.line_price ?? 0;

    if (original != null && original !== finalLine) {
      return `<div class="cart-item__discounted-prices">
        <span class="visually-hidden">Regular price</span>
        <s class="cart-item__old-price price price--end">${this.formatDisplayMoney(original)}</s>
        <span class="visually-hidden">Sale price</span>
        <span class="price price--end">${this.formatDisplayMoney(finalLine)}</span>
      </div>`;
    }

    return `<span class="price price--end">${this.formatDisplayMoney(finalLine)}</span>`;
  },

  buildLineItemHTML(item, line) {
    const title = this.escapeHtml(item.product_title || item.title || '');
    const url = this.escapeHtml(item.url || '#');
    const image = item.featured_image || item.image;
    const imageUrl = image?.url ? this.escapeHtml(image.url) : '';
    const imageAlt = this.escapeHtml(image?.alt || title);
    const imageHeight = image?.aspect_ratio ? Math.ceil(150 / image.aspect_ratio) : 150;
    const linePrice = item.final_line_price ?? item.line_price ?? 0;
    const unitPrice = item.final_price ?? item.price ?? 0;
    const quantity = item.quantity || 1;
    const variantId = item.variant_id || item.id;

    const imageHTML = imageUrl
      ? `<a href="${url}" class="cart-item__link" tabindex="-1" aria-hidden="true"> </a>
         <img class="cart-item__image" src="${imageUrl}" alt="${imageAlt}" loading="lazy" width="150" height="${imageHeight}">`
      : '';

    return `
      <tr
        id="CartDrawer-Item-${line}"
        class="cart-item nihooya-cart-drawer__line-item"
        role="row"
        data-unit-price="${unitPrice}"
        data-line-price="${linePrice}"
      >
        <td class="cart-item__media" role="cell">${imageHTML}</td>
        <td class="cart-item__details" role="cell">
          <div class="nihooya-cart-drawer__line-top">
            <a href="${url}" class="cart-item__name h4 break">${title}</a>
          </div>
          ${this.buildUnitPriceHTML(item)}
          ${this.buildLineDiscountsHTML(item)}
        </td>
        <td class="cart-item__totals right" role="cell">
          <div class="cart-item__price-wrapper">
            ${this.buildLinePriceHTML(item)}
          </div>
        </td>
        <td class="cart-item__quantity" role="cell">
          <div class="cart-item__quantity-wrapper">
            <quantity-input class="quantity cart-quantity">
              <button class="quantity__button no-js-hidden" name="minus" type="button">
                <span class="visually-hidden">Decrease quantity</span>
                ${this.minusIcon}
              </button>
              <input
                class="quantity__input"
                type="number"
                data-quantity-variant-id="${variantId}"
                name="updates[]"
                value="${quantity}"
                data-cart-quantity="${quantity}"
                min="0"
                step="1"
                aria-label="Quantity"
                id="Drawer-quantity-${line}"
                data-index="${line}"
              >
              <button class="quantity__button no-js-hidden" name="plus" type="button">
                <span class="visually-hidden">Increase quantity</span>
                ${this.plusIcon}
              </button>
            </quantity-input>
            <cart-remove-button id="CartDrawer-Remove-${line}" data-index="${line}">
              <button type="button" class="button button--tertiary nihooya-cart-drawer__remove" aria-label="Remove ${title}">
                <span aria-hidden="true">Remove</span>
              </button>
            </cart-remove-button>
          </div>
          <div id="CartDrawer-LineItemError-${line}" class="cart-item__error" role="alert" hidden>
            <small class="cart-item__error-text"></small>
          </div>
        </td>
      </tr>
    `;
  },

  updateLineItemFromCart(row, item, line) {
    if (!row || !item) return;

    const unitPrice = item.final_price ?? item.price ?? 0;
    const linePrice = item.final_line_price ?? item.line_price ?? 0;
    const quantity = item.quantity || 1;

    row.id = `CartDrawer-Item-${line}`;
    row.dataset.unitPrice = String(unitPrice);
    row.dataset.linePrice = String(linePrice);

    const qtyInput = row.querySelector('.quantity__input');
    if (qtyInput) {
      qtyInput.value = quantity;
      qtyInput.id = `Drawer-quantity-${line}`;
      qtyInput.dataset.index = String(line);
    }

    const removeBtn = row.querySelector('cart-remove-button');
    if (removeBtn) {
      removeBtn.id = `CartDrawer-Remove-${line}`;
      removeBtn.dataset.index = String(line);
    }

    const errorEl = row.querySelector('.cart-item__error');
    if (errorEl) errorEl.id = `CartDrawer-LineItemError-${line}`;

    const details = row.querySelector('.cart-item__details');
    if (details) {
      details.querySelectorAll('.nihooya-cart-drawer__unit-price, ul.discounts').forEach((el) => el.remove());
      const options = details.querySelector('dl');
      const extraHTML = this.buildUnitPriceHTML(item) + this.buildLineDiscountsHTML(item);
      if (extraHTML) {
        if (options) options.insertAdjacentHTML('beforebegin', extraHTML);
        else details.insertAdjacentHTML('beforeend', extraHTML);
      }
    }

    const priceWrapper = row.querySelector('.cart-item__price-wrapper');
    if (priceWrapper) {
      priceWrapper.innerHTML = this.buildLinePriceHTML(item);
    }
  },

  rebuildLinesFromItems(items) {
    const tbody = this.ensureDrawerReady();
    if (!tbody || !items?.length) return;

    tbody.innerHTML = items.map((item, index) => this.buildLineItemHTML(item, index + 1)).join('');

    const checkoutButton = this.getDrawer()?.querySelector('#CartDrawer-Checkout');
    if (checkoutButton) checkoutButton.disabled = false;
  },

  syncDrawerFromCart(cart) {
    if (!cart) return;

    const version = ++this._cartSyncVersion;
    this.setSubtotalPending(false);
    this.updateCount(cart.item_count);
    this.updateDrawerFooter(cart);

    const cartDrawer = this.getDrawer();
    if (!cartDrawer) return;

    if (cart.item_count === 0) {
      cartDrawer.classList.add('is-empty');
      const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
      if (cartDrawerItems) cartDrawerItems.classList.add('is-empty');
      const wrapper = cartDrawer.querySelector('[data-cart-drawer-items]');
      if (wrapper) wrapper.hidden = true;
      const emptyPanel = cartDrawer.querySelector('.drawer__inner-empty');
      if (emptyPanel) emptyPanel.hidden = false;
      this.closeDrawerIfEmpty(0);
      return;
    }

    if (version !== this._cartSyncVersion) return;

    cartDrawer.classList.remove('is-empty');
    this.ensureDrawerReady();

    const items = cart.items || [];
    const tbody = cartDrawer.querySelector('.cart-items tbody');
    const domRows = tbody ? tbody.querySelectorAll('.cart-item').length : 0;

    if (!tbody || domRows !== items.length) {
      this.rebuildLinesFromItems(items);
      return;
    }

    items.forEach((item, index) => {
      const line = index + 1;
      const row = document.getElementById(`CartDrawer-Item-${line}`);
      if (!row) {
        this.rebuildLinesFromItems(items);
        return;
      }
      this.updateLineItemFromCart(row, item, line);
    });
  },

  reconcileCart(cart) {
    this.syncDrawerFromCart(cart);
  },

  patchDrawerFromCart(cart) {
    this.syncDrawerFromCart(cart);
  },

  getTotalQuantityFromDOM() {
    let total = 0;
    document.querySelectorAll('.nihooya-cart-drawer .quantity__input').forEach((input) => {
      total += parseInt(input.value || '0', 10) || 0;
    });
    return total;
  },

  closeDrawerIfEmpty(itemCount) {
    const count = itemCount !== undefined ? parseInt(String(itemCount), 10) : this.getTotalQuantityFromDOM();
    const cartDrawer = this.getDrawer();
    if (!cartDrawer) return;

    if (count === 0) {
      cartDrawer.classList.add('is-empty');
      if (cartDrawer.classList.contains('active')) {
        cartDrawer.close();
      }
    }
  },

  optimisticQuantityChange(line, newQty) {
    const row = document.getElementById(`CartDrawer-Item-${line}`);
    if (!row) return;

    const parsedQty = parseInt(String(newQty), 10);
    if (Number.isNaN(parsedQty)) return;

    if (parsedQty <= 0) {
      row.remove();
      this.updateCount(this.getTotalQuantityFromDOM());
      this.setSubtotalPending(true);
      if (document.querySelectorAll('.nihooya-cart-drawer .cart-item').length === 0) {
        this.closeDrawerIfEmpty(0);
      }
      return;
    }

    const qtyInput = row.querySelector('.quantity__input');
    const unitCents = this.getRowUnitCents(row);

    if (qtyInput) qtyInput.value = parsedQty;

    if (unitCents > 0) {
      const estimatedLine = unitCents * parsedQty;
      row.dataset.linePrice = String(estimatedLine);
      const priceWrapper = row.querySelector('.cart-item__price-wrapper');
      if (priceWrapper) {
        priceWrapper.innerHTML = `<span class="price price--end">${this.formatDisplayMoney(estimatedLine)}</span>`;
      }
      this.updateUnitPriceLabel(row, parsedQty, unitCents);
    }

    this.updateCount(this.getTotalQuantityFromDOM());
    this.setSubtotalPending(true);
  },

  updateUnitPriceLabel(row, quantity, unitCents) {
    const details = row.querySelector('.cart-item__details');
    if (!details) return;

    let unitPrice = row.querySelector('.nihooya-cart-drawer__unit-price');
    if (quantity > 1) {
      if (!unitPrice) {
        unitPrice = document.createElement('div');
        unitPrice.className = 'nihooya-cart-drawer__unit-price product-option';
        const top = details.querySelector('.nihooya-cart-drawer__line-top');
        if (top?.nextSibling) details.insertBefore(unitPrice, top.nextSibling);
        else details.appendChild(unitPrice);
      }
      unitPrice.textContent = `${this.formatDisplayMoney(unitCents)} each`;
    } else if (unitPrice) {
      unitPrice.remove();
    }
  },

  optimisticRemove(line) {
    this.optimisticQuantityChange(line, 0);
  },

  optimisticAddFromForm(form, quantity) {
    const tbody = this.ensureDrawerReady();
    if (!tbody) return;

    const variantId = form.querySelector('[name="id"]')?.value;
    const existingInput = variantId
      ? tbody.querySelector(`.quantity__input[data-quantity-variant-id="${variantId}"]`)
      : null;

    if (existingInput) {
      const line = parseInt(existingInput.dataset.index || '0', 10);
      const nextQty = (parseInt(existingInput.value || '0', 10) || 0) + quantity;
      this.optimisticQuantityChange(line, nextQty);
      return;
    }

    const scope = form.closest('.nihooya-product-card, .product, quick-add-modal, main-product');
    let title = 'Added item';
    let url = '#';
    let imageUrl = '';
    let unitCents = 0;

    if (scope) {
      title = scope.querySelector('.nihooya-product-card__title, .product__title, h1')?.textContent?.trim() || title;
      url = scope.querySelector('a[href*="/products/"]')?.href || url;
      const imageEl = scope.querySelector(
        '.nihooya-product-card__image--primary, .product__media img, .product__modal-opener img'
      );
      imageUrl = imageEl?.currentSrc || imageEl?.src || '';
      unitCents = this.parseMoneyFromDisplay(
        scope.querySelector('.nihooya-product-card__price, .price-item--regular, .price')?.textContent
      );
    }

    const line = tbody.querySelectorAll('.cart-item').length + 1;
    const tempItem = {
      product_title: title,
      title,
      url,
      quantity,
      variant_id: variantId,
      featured_image: imageUrl ? { url: imageUrl, alt: title, aspect_ratio: 1 } : null,
      final_line_price: unitCents * quantity,
      line_price: unitCents * quantity,
      final_price: unitCents,
      price: unitCents,
      discounts: []
    };

    if (line === 1 && !tbody.querySelector('.cart-item')) {
      tbody.innerHTML = this.buildLineItemHTML(tempItem, 1);
    } else {
      tbody.insertAdjacentHTML('beforeend', this.buildLineItemHTML(tempItem, line));
    }

    this.updateCount(this.getTotalQuantityFromDOM());
    this.setSubtotalPending(true);

    const checkoutButton = this.getDrawer()?.querySelector('#CartDrawer-Checkout');
    if (checkoutButton) checkoutButton.disabled = false;
  },

  parseCartChangeResponse(state) {
    let parsedState = state;
    if (typeof state === 'string') {
      try {
        parsedState = JSON.parse(state);
      } catch (error) {
        return { errors: window.cartStrings?.error || 'Cart update failed' };
      }
    }

    if (!parsedState) return null;

    if (parsedState.status && parsedState.status >= 400) {
      return {
        errors: parsedState.description || parsedState.message || window.cartStrings?.error || 'Cart update failed'
      };
    }

    if (parsedState.errors) return parsedState;

    return parsedState;
  },

  changeLine(line, quantity) {
    const body = JSON.stringify({
      line,
      quantity,
      sections_url: window.location.pathname
    });

    return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsed = this.parseCartChangeResponse(state);
        if (parsed?.errors) return parsed;
        return this.fetchCart();
      });
  },

  debouncedChangeLine(line, quantity, delay = 80) {
    if (this._changeTimers[line]) clearTimeout(this._changeTimers[line]);

    return new Promise((resolve, reject) => {
      this._changeTimers[line] = setTimeout(() => {
        delete this._changeTimers[line];
        this.changeLine(line, quantity).then(resolve).catch(reject);
      }, delay);
    });
  },

  fetchCart() {
    return fetch(`${routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }).then((response) => response.json());
  },

  isSoldOutError(message) {
    return /sold out|unavailable|not enough|inventory|stock/i.test(String(message || ''));
  }
};